import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Alert, IconButton, CircularProgress, Chip, Button,
} from '@mui/material';
import { Home, EventBusy, NotificationsActive } from '@mui/icons-material';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL;

export default function OverdueBooks() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const [reminding, setReminding] = useState(false);
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const remind = async () => {
    setReminding(true); setOk(''); setError('');
    try {
      const { data } = await axios.post(`${API}/api/library/loans/remind-overdue`, {}, { headers });
      setOk(`Sent ${data.sent} reminder(s) for ${data.overdue} overdue book(s).`);
    } catch (err) { setError(err.response?.data?.error || 'Failed to send reminders'); }
    finally { setReminding(false); }
  };

  useEffect(() => {
    axios.get(`${API}/api/library/loans/overdue`, { headers })
      .then(r => setRows(r.data))
      .catch(err => setError(err.response?.data?.error || 'Failed to load'))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const daysOver = (due) => Math.max(0, Math.floor((Date.now() - new Date(due)) / 86400000));

  if (loading) return (<Box sx={{ maxWidth: 1000, mx: 'auto', mt: 4, textAlign: 'center' }}><CircularProgress /></Box>);

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center"><EventBusy sx={{ mr: 1, color: '#c62828' }} /><Typography variant="h5" sx={{ color: '#c62828' }}>Overdue Books</Typography></Box>
            <Box>
              <Button variant="outlined" color="warning" startIcon={<NotificationsActive />} sx={{ mr: 1 }}
                disabled={rows.length === 0 || reminding} onClick={remind}>
                {reminding ? 'Sending…' : 'Remind guardians'}
              </Button>
              <IconButton onClick={() => navigate('/library')} title="Library Dashboard"><Home /></IconButton>
            </Box>
          </Box>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          {ok && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setOk('')}>{ok}</Alert>}
          {rows.length === 0 ? <Alert severity="success">No overdue books. 🎉</Alert> : (
            <Table size="small">
              <TableHead><TableRow>
                <TableCell><strong>Loan</strong></TableCell><TableCell><strong>Book</strong></TableCell><TableCell><strong>Borrower</strong></TableCell>
                <TableCell><strong>Due</strong></TableCell><TableCell align="right"><strong>Days overdue</strong></TableCell>
              </TableRow></TableHead>
              <TableBody>
                {rows.map(l => (
                  <TableRow key={l.loanNo} hover>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{l.loanNo}</TableCell>
                    <TableCell>{l.bookTitle}</TableCell>
                    <TableCell>{l.borrowerName || l.borrowerRef} <Chip label={l.borrowerType} size="small" variant="outlined" sx={{ ml: 0.5 }} /></TableCell>
                    <TableCell>{l.dueDate ? new Date(l.dueDate).toLocaleDateString() : ''}</TableCell>
                    <TableCell align="right"><Chip label={daysOver(l.dueDate)} size="small" color="error" /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
